/**
 * The 3D preview in the designer.
 *
 * PLAN-3D.md §8. Shown only when the product's 3D is switched on and complete
 * for the chosen variation (config.three). It turns to face the page being
 * edited. A panel beside the canvas on a wide screen;
 * the whole screen on a phone, with the design one tap away. three.js and the
 * model are fetched the first time it opens, never before.
 */

import { useEffect, useRef, useState } from 'react';
import { WallPhoto } from './WallPhoto';
import { RoomView } from './RoomView';
import type { ThreeConfig } from '../three/types';
import type { Viewer, ViewName } from '../three/scene';
import { useEditor } from '../state/store';
import type { Page } from '../model/types';

/** How long after the last edit the product is repainted. */
const SETTLE_MS = 250;

/** Load times and a speed test, for checking a device: add ?tb3d=debug to the designer's address. */
const debug = () => new URLSearchParams( window.location.search ).get( 'tb3d' ) === 'debug';

/**
 * Too small to show the design and the product side by side.
 *
 * Exported because the app has to agree: there the preview is the whole screen
 * rather than a column, so the grid must not keep a column open behind it.
 */
export const isPhone = () =>
	window.matchMedia( '(max-width: 900px), (pointer: coarse) and (max-width: 1100px)' ).matches;

export function ThreeView( { config, onClose }: { config: ThreeConfig; onClose: () => void } ) {
  const host = useRef<HTMLDivElement>( null );
  const viewer = useRef<Viewer | null>( null );
  const drawn = useRef( new Map<number, Page>() );
  const [ state, setState ] = useState<'loading' | 'ready' | 'failed'>( 'loading' );
  const [ stats, setStats ] = useState( '' );
  const [ scene, setScene ] = useState<'studio' | 'wall'>( 'studio' );
  const [ photo, setPhoto ] = useState<HTMLImageElement | null>( null );

  /*
   * The shop's rooms first, when this canvas is offered in any; 3D otherwise.
   *
   * A customer choosing wall art is asking whether it will look right in a
   * room, and a room answers that. 3D is one click away for turning the
   * canvas round to see its edges.
   */
  const rooms = config.type === 'canvas' ? config.rooms ?? [] : [];
  const [ mode, setMode ] = useState<'2d' | '3d'>( rooms.length ? '2d' : '3d' );

  /*
   * How wide the preview's half is, in pixels, remembered between visits.
   *
   * It opens as two halves and is dragged from there. The number is written on
   * the app rather than on this element, because the split is a column of the
   * grid now: the canvas has to shrink by exactly what the preview grows, and
   * a width set here would leave the grid's idea of the split untouched.
   *
   * Null means "whatever the stylesheet says", which is half.
   */
  const [ width, setWidth ] = useState<number | null>( () => {
    try {
      const kept = Number( window.localStorage.getItem( 'trimbox.three.width' ) );

      return kept > 0 ? kept : null;
    } catch {
      return null;
    }
  } );
  const phone = useRef( isPhone() );
  const doc = useEditor( ( s ) => s.doc );
  const pageIndex = useEditor( ( s ) => s.pageIndex );

  /*
   * The split lives on the app, because it is the grid that divides the two.
   * Cleared on the way out so closing the preview gives the whole width back
   * rather than leaving a variable behind for the next time it opens.
   */
  useEffect( () => {
    if ( phone.current ) return;

    const app = document.querySelector( '.tb-app' ) as HTMLElement | null;

    if ( ! app ) return;

    if ( width ) app.style.setProperty( '--tb-three-w', `${ width }px` );
    else app.style.removeProperty( '--tb-three-w' );

    return () => {
      app.style.removeProperty( '--tb-three-w' );
    };
  }, [ width ] );

  /*
   * Open: fetch the viewer, build the product, paint every page - the first
   * time 3D is looked at, not before.
   *
   * With a room showing first, three.js and the model are a download nobody
   * has asked for yet; on a phone that is most of a second and a few hundred
   * kilobytes. Once made the viewer is kept, so going back and forth between
   * the room and 3D is instant.
   */
  const started = useRef( false );
  const closed = useRef( false );

  /*
   * Open again on (re)mounting. StrictMode mounts, unmounts and mounts again
   * in development; without this the flag stayed set from the pretend unmount
   * and 3D never loaded at all.
   */
  useEffect( () => {
    closed.current = false;

    return () => {
      closed.current = true;
      viewer.current?.dispose();
      viewer.current = null;
      drawn.current.clear();
    };
  }, [] );

  useEffect( () => {
    if ( mode !== '3d' || started.current ) return;

    started.current = true;

    const opened = performance.now();

    ( async () => {
      try {
        if ( ! host.current ) return;

        const { createViewer } = await import( '../three/scene' );
        const chunkMs = Math.round( performance.now() - opened );

        if ( closed.current || ! host.current ) return;

        const made = await createViewer( host.current, config, phone.current );

        if ( closed.current ) {
          made.dispose();

          return;
        }

        viewer.current = made;

        const painted = await paintAll( true );

        // The page being edited, from the angle a product photograph takes:
        // face-on hides the handle, and then it hardly looks like a mug.
        made.face( useEditor.getState().pageIndex, true );
        setState( 'ready' );
        setStats(
          `Code ${ chunkMs } ms · model ${ made.stats.loadMs } ms · design ${ painted } ms · ` +
            `${ made.textureSize }px${ made.stats.lowPower ? ' · low power' : '' }`
        );

        ( window as unknown as { __tb3d?: unknown } ).__tb3d = { chunkMs, painted, ...made.stats, textureSize: made.textureSize, viewer: made };
      } catch ( error ) {
        console.error( '[trimbox] 3D preview failed', error );
        if ( ! closed.current ) setState( 'failed' );
      }
    } )();
  }, [ mode ] ); // eslint-disable-line react-hooks/exhaustive-deps

  // Another page chosen in the designer: turn to where it prints.
  useEffect( () => {
    if ( state === 'ready' && config.areas.some( ( a ) => a.page === pageIndex ) ) viewer.current?.face( pageIndex, true );
  }, [ pageIndex, state ] ); // eslint-disable-line react-hooks/exhaustive-deps

  // Edits: repaint the pages that changed, a moment after the customer pauses.
  useEffect( () => {
    if ( state !== 'ready' ) return;

    const timer = window.setTimeout( () => void paintAll( false ), SETTLE_MS );

    return () => window.clearTimeout( timer );
  }, [ doc, state ] ); // eslint-disable-line react-hooks/exhaustive-deps

  async function paintAll( force: boolean ): Promise<number> {
    const current = useEditor.getState().doc;
    const view = viewer.current;
    const started = performance.now();

    if ( ! current || ! view ) return 0;

    const { pageTexture } = await import( '../three/texture' );
    const counter = window as unknown as { __tb3dPaints?: number };

    counter.__tb3dPaints = ( counter.__tb3dPaints ?? 0 ) + 1;

    for ( const area of config.areas ) {
      const page = current.pages[ area.page ];

      if ( ! page || ( ! force && drawn.current.get( area.page ) === page ) ) continue;

      const canvas = await pageTexture( current, area.page, Math.round( view.textureSize * 0.6 ) );

      if ( canvas && viewer.current === view ) {
        view.setPage( area.page, canvas );
        drawn.current.set( area.page, page );
      }
    }

    return Math.round( performance.now() - started );
  }

  const views: Array<[ ViewName, string ]> = config.type === 'mug'
    ? [ [ 'angle', 'Angle' ], [ 'front', 'Front' ], [ 'side', 'Side' ], [ 'back', 'Handle side' ] ]
    : [ [ 'front', 'Front' ], [ 'back', 'Back' ], [ 'side', 'Side' ], [ 'angle', 'Angle' ] ];

  return (
    <section
      className={ `tb-three${ phone.current ? ' tb-three--phone' : '' }` }
      aria-label="3D preview"
    >
      { ! phone.current && (
        <div
          className="tb-three__grip"
          role="separator"
          aria-label="Panel width"
          aria-orientation="vertical"
          onPointerDown={ ( event ) => {
            event.preventDefault();
            ( event.target as HTMLElement ).setPointerCapture( event.pointerId );

            const panel = ( event.currentTarget.parentElement as HTMLElement );
            const app = panel.closest( '.tb-app' ) as HTMLElement | null;
            const from = event.clientX;
            const was = panel.getBoundingClientRect().width;

            if ( ! app ) return;

            /*
             * What the designer must keep, whatever the drag asks for: the tool
             * rail, the canvas minimum, and the tool panel when it is holding a
             * column of its own. Read from the stylesheet rather than repeated
             * here, so the floor is one number in one place.
             */
            const styles = window.getComputedStyle( app );
            const floor = ( name: string ) => parseFloat( styles.getPropertyValue( name ) ) || 0;
            const taken = app.getBoundingClientRect().width - panel.getBoundingClientRect().width;
            const most = Math.max( 320, taken - floor( '--tb-canvas-min' ) + was );

            /*
             * Dragged left is wider: the preview is the right-hand column, so
             * it grows as the grip travels away from that edge.
             */
            const move = ( at: PointerEvent ) => {
              const next = Math.round( Math.min( most, Math.max( 320, was + ( from - at.clientX ) ) ) );

              setWidth( next );
            };

            const done = () => {
              window.removeEventListener( 'pointermove', move );
              window.removeEventListener( 'pointerup', done );

              // The viewer watches its host with a ResizeObserver and the
              // canvas watches its own box, so both follow the column without
              // being told anything.
              try {
                window.localStorage.setItem( 'trimbox.three.width', String( Math.round( panel.getBoundingClientRect().width ) ) );
              } catch {
                // Not remembered; the drag itself still worked.
              }
            };

            window.addEventListener( 'pointermove', move );
            window.addEventListener( 'pointerup', done );
          } }
        />
      ) }
      <div className="tb-three__head">
        { rooms.length ? (
          <span className="tb-three__modes" role="group" aria-label="Preview">
            <button type="button" className={ `tb-three__mode${ mode === '2d' ? ' is-on' : '' }` } aria-pressed={ mode === '2d' } onClick={ () => setMode( '2d' ) }>
              2D preview
            </button>
            <button type="button" className={ `tb-three__mode${ mode === '3d' ? ' is-on' : '' }` } aria-pressed={ mode === '3d' } onClick={ () => setMode( '3d' ) }>
              3D preview
            </button>
          </span>
        ) : (
          <h2 className="tb-three__title">3D preview</h2>
        ) }
        <button type="button" className="tb-btn tb-btn--quiet tb-three__close" onClick={ onClose }>
          { phone.current ? 'Back to design' : 'Close' }
        </button>
      </div>

      { mode === '2d' && <RoomView config={ config } rooms={ rooms } /> }

      { /*
         * Kept in the page while the room shows, only hidden: the viewer is
         * drawn into this element, and taking it away would leave the viewer
         * drawing into nothing when the customer comes back to 3D.
         */ }
      <div className="tb-three__stage" ref={ host } hidden={ mode === '2d' }>
        { state === 'loading' && <p className="tb-three__note">Loading 3D…</p> }
        { state === 'failed' && <p className="tb-three__note">3D preview is not available on this device.</p> }

        { /*
           * Over the 3D stage rather than instead of it: the viewer keeps its
           * context and its textures, so going back to the plain wall is
           * instant and nothing has to be rebuilt.
           */ }
        { photo && config.type === 'canvas' && (
          <WallPhoto config={ config } photo={ photo } />
        ) }
      </div>

      { mode === '3d' && state === 'ready' && ! photo && (
        <div className="tb-three__views" role="group" aria-label="Views">
          { views.map( ( [ name, label ] ) => (
            <button key={ name } type="button" className="tb-btn tb-btn--quiet" data-view={ name } onClick={ () => viewer.current?.view( name ) }>
              { label }
            </button>
          ) ) }
          { debug() && <button
            type="button"
            className="tb-btn tb-btn--quiet"
            onClick={ async () => {
              const fps = await viewer.current?.measureFps();

              setStats( ( was ) => `${ was.replace( / · \d+ fps$/, '' ) } · ${ fps } fps` );
            } }
          >
            Test speed
          </button> }
        </div>
      ) }

      { mode === '3d' && state === 'ready' && config.type === 'canvas' && (
        <div className="tb-three__wall">
          <div className="tb-three__views" role="group" aria-label="Where it is shown">
            { ( [ [ 'studio', 'On its own' ], [ 'wall', 'On a wall' ] ] as const ).map( ( [ name, label ] ) => (
              <button
                key={ name }
                type="button"
                className={ `tb-btn tb-btn--quiet${ scene === name ? ' is-on' : '' }` }
                data-scene={ name }
                aria-pressed={ scene === name }
                onClick={ () => {
                  setScene( name );
                  viewer.current?.setScene( name );
                } }
              >
                { label }
              </button>
            ) ) }
          </div>

          { scene === 'wall' && (
            /*
             * A row, not a label wrapping everything.
             *
             * "Plain wall" used to sit inside the label, so pressing it opened
             * the file picker as well as clearing the photograph - a label
             * forwards a click to its control wherever in it you press. Only
             * the picker is a label now; the button beside it is its own
             * element.
             */
            <div className="tb-three__photo">
              <label className="tb-btn tb-three__pick">
                <span>{ photo ? 'Change photo' : 'Use a photo of your wall' }</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={ ( event ) => {
                    const file = event.target.files?.[ 0 ];

                    if ( ! file ) return;

                    /*
                     * Kept in this tab and nowhere else. The photograph is a
                     * way of looking at the picture, not part of the order, so
                     * it is never uploaded and never saved - which also means
                     * there is nothing to explain about what happens to a
                     * photograph of somebody's living room.
                     */
                    const image = new Image();

                    image.onload = () => setPhoto( image );

                    image.src = URL.createObjectURL( file );

                    // So choosing the same file twice still fires a change.
                    event.target.value = '';
                  } }
                />
              </label>

              { photo && (
                <button type="button" className="tb-btn tb-btn--quiet" onClick={ () => setPhoto( null ) }>
                  Plain wall
                </button>
              ) }
            </div>
          ) }
        </div>
      ) }

      { state === 'ready' && config.mug?.finish === 'magic' && (
        <label className="tb-three__reveal">
          <span>Heat to reveal</span>
          <input
            type="range"
            min={ 0 }
            max={ 100 }
            defaultValue={ 100 }
            onChange={ ( event ) => viewer.current?.setReveal( Number( event.target.value ) / 100 ) }
          />
        </label>
      ) }

      { /* Turning and zooming are 3D's; a room is a flat photograph. */ }
      { mode === '3d' && ! photo && (
        <p className="tb-three__hint">{ phone.current ? 'Drag to turn · pinch to zoom' : 'Drag to turn · scroll to zoom' }</p>
      ) }

      { /*
         * Said plainly, and said here rather than only in the terms.
         *
         * A 3D preview is a lit, idealised render on whatever screen the
         * customer happens to have. It is honest about colour and about scale
         * and it is not a contract; the flat proof is. Customers who are told
         * that before they buy do not raise it afterwards.
         */ }
      <p className="tb-three__notice">
        { mode === '2d'
          ? 'This room is a guide to size and colour. The room’s colours are approximate, as is how your design looks on screen, and the printed item may differ. Your flat proof is the approved artwork.'
          : 'This 3D view is a guide to how your design sits on the product. Screen colours, lighting, finish and 3D model are approximate, and the printed item may differ. Your flat proof is the approved artwork.' }
      </p>
      { stats && debug() && <p className="tb-three__stats">{ stats }</p> }
    </section>
  );
}
